import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  dashboardRequest,
  isDashboardDataProviderEnabled,
} from "../providers/aws/client";
import {
  GALLERY_FUNNEL_WINDOWS,
  furthestStageLabel,
  galleryFunnelPath,
  galleryFunnelRows,
  type GalleryFunnel,
  type GalleryFunnelView,
} from "./broadcastGalleryFunnel";

/** Who came to my.echoflicks.com after this broadcast and what they did
 *  there, from the gallery's own journey events. */
export function BroadcastGalleryFunnelCard({
  broadcastId,
}: {
  broadcastId: string | number;
}) {
  const enabled = isDashboardDataProviderEnabled();
  const [windowDays, setWindowDays] = useState<number>(7);
  const [refreshing, setRefreshing] = useState(false);
  const query = useQuery({
    queryKey: ["broadcast-gallery-funnel", broadcastId, windowDays],
    enabled,
    queryFn: () =>
      dashboardRequest<{ data: GalleryFunnel }>(
        galleryFunnelPath(broadcastId, windowDays),
      ),
  });
  if (!enabled) return null;

  async function refresh() {
    setRefreshing(true);
    try {
      await dashboardRequest(galleryFunnelPath(broadcastId, windowDays, true));
      await query.refetch();
    } finally {
      setRefreshing(false);
    }
  }

  const funnel = query.data?.data;
  return (
    <Card aria-label="Gallery after this broadcast">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Gallery after this broadcast</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            What recipients did on my.echoflicks.com. Time on site is not
            measured; a visit is activity with no gap over 30 minutes.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {GALLERY_FUNNEL_WINDOWS.map((days) => (
            <Button
              key={days}
              size="sm"
              variant={days === windowDays ? "secondary" : "ghost"}
              onClick={() => setWindowDays(days)}
            >
              {days}d
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={refreshing || query.isFetching}
            aria-label="Recompute"
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {query.isError && (
          <p className="text-sm text-muted-foreground">
            Could not load the gallery funnel. Try again in a moment.
          </p>
        )}
        {!funnel && query.isLoading && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {funnel && (
          <>
            <div className="grid gap-6 lg:grid-cols-2">
              <FunnelColumn
                title="Through the tracked link"
                help="Only taps on this broadcast's own tracked link or button. Exact."
                funnel={funnel}
                view={funnel.tracked}
              />
              <FunnelColumn
                title={`Any visit within ${funnel.window_days} days`}
                help="Signed-in activity after the message, however they arrived (a static button, a bookmark). Timing, not proof."
                funnel={funnel}
                view={funnel.after_send}
              />
            </div>
            <PeopleTable funnel={funnel} />
            <p className="text-xs text-muted-foreground">
              {funnel.recipients_sent} recipients sent. Updated{" "}
              {new Date(funnel.computed_at).toLocaleString()}.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelColumn({
  title,
  help,
  funnel,
  view,
}: {
  title: string;
  help: string;
  funnel: GalleryFunnel;
  view: GalleryFunnelView;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      {galleryFunnelRows(funnel, view).map((row) => (
        <div
          key={row.key}
          className="grid grid-cols-[9rem_minmax(0,1fr)_5rem] items-center gap-3"
        >
          <span className="text-sm">{row.label}</span>
          <Progress value={row.percent} />
          <span className="text-right text-sm tabular-nums">
            {row.value} {row.value ? `(${Math.round(row.percent)}%)` : ""}
          </span>
        </div>
      ))}
      <div className="grid grid-cols-4 gap-2 pt-1 text-center">
        <Stat label="People" value={view.people} />
        <Stat label="Visits" value={view.visits} />
        <Stat label="Plays" value={view.plays} />
        <Stat label="Min watched" value={view.watch_minutes} />
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function PeopleTable({ funnel }: { funnel: GalleryFunnel }) {
  if (!funnel.people.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No recipient has been to the gallery since this broadcast yet.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">People ({funnel.people.length})</h4>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead>Got as far as</TableHead>
            <TableHead className="text-right">Visits</TableHead>
            <TableHead className="text-right">Plays</TableHead>
            <TableHead className="text-right">Min watched</TableHead>
            <TableHead>First seen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {funnel.people.map((person) => (
            <TableRow key={person.contact_id}>
              <TableCell>
                <Link
                  to={`/contacts/${encodeURIComponent(person.contact_id)}/show`}
                  className="text-primary hover:underline"
                >
                  …{person.contact_id.slice(-4)}
                </Link>{" "}
                {person.tracked && (
                  <Badge variant="secondary" className="ml-1">
                    tracked
                  </Badge>
                )}
              </TableCell>
              <TableCell>{furthestStageLabel(funnel, person)}</TableCell>
              <TableCell className="text-right tabular-nums">
                {person.visits}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {person.plays}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {person.watch_minutes}
              </TableCell>
              <TableCell>
                {new Date(person.first_activity_at).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
